import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { History, SquarePen } from 'lucide-react';
import { AlmaOrb } from '../components/AlmaOrb';
import { ChatPanel } from '../components/chat/ChatPanel';
import { SessionListSheet } from '../components/chat/SessionListSheet';

/**
 * Alma page: one chat conversation (transcript + composer) plus the session
 * chrome — "New chat" and "History" (the durable session list).
 *
 * Routing: `/alma` starts fresh; `/alma/:sessionId` opens an existing
 * conversation. When the FIRST send creates a session, the URL is updated
 * with `history.replaceState` (not a router navigation) so the address bar
 * deep-links to the conversation WITHOUT remounting the page mid-stream —
 * Layout keys the page container on pathname, so a real navigation would
 * abort the live turn.
 */
export function AlmaPage() {
  const { sessionId: routeSessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [createdSessionId, setCreatedSessionId] = useState<string | undefined>(undefined);
  /** Bumped to remount the panel for "New chat" without a route change. */
  const [panelKey, setPanelKey] = useState(0);

  const activeSessionId = routeSessionId ?? createdSessionId;

  const handleSessionCreated = (id: string): void => {
    setCreatedSessionId(id);
    window.history.replaceState(window.history.state, '', `/alma/${id}`);
  };

  const startNewChat = (): void => {
    setCreatedSessionId(undefined);
    if (routeSessionId) {
      navigate('/alma');
    } else {
      window.history.replaceState(window.history.state, '', '/alma');
      setPanelKey((k) => k + 1);
    }
  };

  const openSession = (id: string): void => {
    setHistoryOpen(false);
    if (id === activeSessionId) return;
    setCreatedSessionId(undefined);
    setPanelKey((k) => k + 1);
    navigate(`/alma/${id}`);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-2">
        {/* The header wordmark already shows "Alma"; keep the page h1 for
            document structure but visually hide it. It says "Chat" to match
            the nav label rather than naming the assistant. */}
        <h1 className="sr-only">Chat</h1>
        <div className="flex items-center gap-2 ml-auto">
          <button type="button" className="btn btn-ghost" onClick={() => setHistoryOpen(true)}>
            <History className="w-4 h-4" aria-hidden />
            History
          </button>
          <button type="button" className="btn btn-ghost" onClick={startNewChat}>
            <SquarePen className="w-4 h-4" aria-hidden />
            New chat
          </button>
        </div>
      </div>

      {!routeSessionId && (
        <section className="flex flex-col items-center text-center pt-2 sm:pt-6 animate-fade-in-up">
          <AlmaOrb size="md" />
          <p className="mt-2 text-[var(--text-secondary)]">
            Write what's happening, or ask about what you've already written.
          </p>
        </section>
      )}

      <ChatPanel
        key={`${routeSessionId ?? 'new'}-${panelKey}`}
        sessionId={routeSessionId}
        onSessionCreated={handleSessionCreated}
      />

      {historyOpen && (
        <SessionListSheet
          onOpenSession={openSession}
          onClose={() => setHistoryOpen(false)}
          activeSessionId={activeSessionId}
        />
      )}
    </div>
  );
}
